import React, { useEffect, useState } from 'react';

interface InactivityWarningProps {
    isVisible: boolean;
    warningDuration: number;
    onContinue: () => void;
    onLogout: () => void;
}

const InactivityWarning: React.FC<InactivityWarningProps> = ({
    isVisible,
    warningDuration,
    onContinue,
    onLogout
}) => {
    const [timeLeft, setTimeLeft] = useState(warningDuration);

    // Reset timer when modal becomes visible
    useEffect(() => {
        if (isVisible) {
            setTimeLeft(warningDuration);
        }
    }, [isVisible, warningDuration]);

    // Handle countdown
    useEffect(() => {
        if (!isVisible || timeLeft <= 0) return;

        const interval = setInterval(() => {
            setTimeLeft(prev => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [isVisible, timeLeft]);

    // Handle auto-logout when time reaches 0
    useEffect(() => {
        if (isVisible && timeLeft === 0) {
            onLogout();
        }
    }, [isVisible, timeLeft, onLogout]);

    if (!isVisible) return null;

    const handleModalClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <div
            className="modal show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}
            onClick={handleModalClick}
        >
            <div className="modal-dialog modal-dialog-centered" onClick={handleModalClick}>
                <div className="modal-content" onClick={handleModalClick}>
                    <div className="modal-header bg-information">
                        <h5 className="modal-title">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            Session Timeout Warning
                        </h5>
                    </div>
                    <div className="modal-body text-center">
                        <div className="alert alert-warning">
                            <strong>You appear to be inactive. For security reasons, you will be automatically logged out in {timeLeft} seconds</strong>
                        </div>
                        <p className="text-muted">
                            Click "Continue Working" if you're still using Essentials.
                        </p>
                    </div>
                    <div className="modal-footer justify-content-center">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onContinue();
                            }}
                            className="btn btn-primary btn-lg me-3"
                        >
                            Continue Working
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onLogout();
                            }}
                            className="btn btn-outline-secondary"
                        >
                            Sign Out Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InactivityWarning;